import type { TIntakeTreeElement } from "@/types/intake";

/**
 * Flatten a Page's element tree to its leaf fields, descending through the
 * structural containers (``field_group`` / ``columns``) which are layout, not
 * fields. The Page stores content as the intake element tree (``page.elements``,
 * #2154/#2271); the interim ``page.fields`` key it replaced is no longer served,
 * so anything reading ``page.fields`` saw an empty list.
 *
 * Single source of truth for "the fields on this page": the Page card counts
 * ``flattenPageFields(...).length`` and the Page config panel lists the same
 * elements, so the summary count and the list never disagree.
 *
 * Also called against the raw workflow snapshot wire shape (#2551's builder
 * Preview walk, ``form-workflow.store.ts``'s ``fieldValuesFromSurveyData``) —
 * a FLAT list where a container's children are separate array entries linked
 * by ``parent_element_id`` rather than nested under ``.children`` / ``.columns``.
 * The `Array.isArray` guards make that shape a no-op recursion (the container
 * itself contributes nothing, correctly, since it carries no value) rather
 * than a crash (`for...of undefined`) — the container's children still get
 * included because they're their own top-level entries in that same flat list.
 */
export function flattenPageFields(
  elements: readonly TIntakeTreeElement[] | undefined,
): TIntakeTreeElement[] {
  const out: TIntakeTreeElement[] = [];
  for (const el of elements ?? []) {
    if (el.kind === "field_group") {
      if (Array.isArray(el.children)) out.push(...flattenPageFields(el.children));
    } else if (el.kind === "columns") {
      if (Array.isArray(el.columns)) {
        for (const column of el.columns) out.push(...flattenPageFields(column.children));
      }
    } else {
      out.push(el);
    }
  }
  return out;
}
